import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, Mail } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { NovatusLogo } from "@/components/NovatusLogo";
import { Button } from "@/components/ui/button";

// /confirm-email?token=...
// Calls backend, shows result. On success: tells user admin has been notified.
export default function ConfirmEmail() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const token = params.get("token") || "";
  const [state, setState] = useState("loading");
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (!token) {
      setState("error");
      setInfo("Kein Bestätigungstoken in der URL");
      return;
    }
    // Force-logout any existing session so the "Login" button after confirm
    // really lands on the login page (not someone else's dashboard).
    logout();
    api.get(`/auth/confirm-email?token=${encodeURIComponent(token)}`)
      .then((r) => {
        setInfo(r.data);
        setState(r.data.already_confirmed ? "already" : "success");
      })
      .catch((e) => {
        setInfo(e.response?.data?.detail || "Bestätigung fehlgeschlagen");
        setState("error");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <div className="min-h-screen bg-[#09090B] flex flex-col items-center justify-center px-6 py-8">
      <div className="w-full max-w-md">
        <div className="mb-6 flex justify-center">
          <NovatusLogo size="md" />
        </div>

        <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-8 text-center">
          {state === "loading" && (
            <>
              <Loader2 className="text-zinc-400 animate-spin mx-auto mb-3" size={32} />
              <p className="text-sm text-zinc-300 font-medium">Bestätigung wird geprüft …</p>
            </>
          )}

          {state === "success" && (
            <>
              <div className="w-16 h-16 bg-[#00E575]/15 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="text-[#00E575]" size={32} />
              </div>
              <h1 className="font-display text-2xl font-semibold mb-2">
                E-Mail bestätigt
              </h1>
              <p className="text-sm text-zinc-400 mb-2 leading-relaxed">
                Vielen Dank{info?.first_name ? `, ${info.first_name}` : ""}!
              </p>
              <p className="text-sm text-zinc-400 mb-6 leading-relaxed">
                Ihre Anfrage wurde nun an den Administrator weitergeleitet.
                Sie erhalten eine zweite E-Mail, sobald Ihr Konto freigeschaltet ist.
              </p>
              <Button
                data-testid="confirm-email-back-btn"
                onClick={() => navigate("/login")}
                className="w-full h-12 bg-[#00E575] text-[#09090B] font-semibold hover:bg-[#00C964]"
              >
                Zum Login
              </Button>
            </>
          )}

          {state === "already" && (
            <>
              <div className="w-16 h-16 bg-sky-500/15 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="text-sky-400" size={28} />
              </div>
              <h1 className="font-display text-2xl font-semibold mb-2">
                Bereits bestätigt
              </h1>
              <p className="text-sm text-zinc-400 mb-6 leading-relaxed">
                Diese E-Mail-Adresse wurde bereits bestätigt. Bitte warten Sie auf
                die Freischaltung durch den Administrator.
              </p>
              <Link to="/login">
                <Button variant="outline" className="w-full h-12 border-zinc-700">
                  Zum Login
                </Button>
              </Link>
            </>
          )}

          {state === "error" && (
            <>
              <div className="w-16 h-16 bg-red-500/15 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="text-red-400" size={32} />
              </div>
              <h1 className="font-display text-2xl font-semibold mb-2">
                Bestätigung fehlgeschlagen
              </h1>
              <p className="text-sm text-zinc-400 mb-6 leading-relaxed">
                {typeof info === "string" ? info : "Der Link ist abgelaufen oder ungültig."}
              </p>
              <Link to="/login">
                <Button variant="outline" className="w-full h-12 border-zinc-700">
                  Zur Anmeldung
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
